import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { RedirectSingleton, RedirectType } from '@pkg/classes';
import { DepButton, DepInput } from '@pkg/components/deprecated';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';
import { validEmail } from '@pkg/utils';

import { apiFactory, Auth, Users } from '@app/client';
import { setAuthTokens, useLogout } from '@app/store/actions/auth';
import { addNotification } from '@app/store/actions/notification';
import { initializeUser } from '@app/store/actions/user';

import { Typography } from '../../DesignSystem';
import PasswordInput from '../../Input/PasswordInput';

const WaitListLoginForm = () => {
    const { t } = useTranslation(['waitlist']);
    const dispatch = useAppDispatch();
    const userId = useAppSelector((rootState) => rootState.user?.userAccount?.user.id);
    const partnerConversionId = useAppSelector((rootState) => rootState.partnerIntegrations.partnerConversionId);

    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [password, setPassword] = useState('');
    const [isInvalid, setInvalid] = useState(false);
    const [isSubmitted, setSubmitted] = useState(false);
    const storedRedirect = RedirectSingleton.getRedirect(RedirectType.Default);
    const logout = useLogout();

    useEffect(() => {
        if (!isSubmitted) {
            return;
        }
        setInvalid(!validEmail(email));
    }, [email, isSubmitted]);

    const goForgotPassword = () => {
        logout(storedRedirect);
    };

    const handleSubmit = () => {
        if (!userId) {
            return;
        }
        setSubmitted(true);
        if (!validEmail(email)) {
            setInvalid(true);
            return;
        }
        setLoading(true);
        Promise.resolve(true)
            .then(() => {
                return apiFactory()
                    .call(Auth.login, {
                        value: {
                            email,
                            password,
                            partner_conversion_id: partnerConversionId,
                        },
                    })
                    .then((res) => {
                        if (res.ok) {
                            dispatch(setAuthTokens(res.value));
                            return;
                        }
                        dispatch(
                            addNotification({
                                message: res.error as string,
                                severity: 'error',
                            }),
                        );
                        return Promise.reject(false);
                    })
                    .catch(() => {
                        dispatch(
                            addNotification({
                                message: t('waitlist_login_failed'),
                            }),
                        );
                        return Promise.reject(false);
                    });
            })
            .then(() => {
                return apiFactory()
                    .call(Users.consumeDraftUser, {
                        value: {
                            userId,
                        },
                    })
                    .then((res) => {
                        if (res.ok) {
                            localStorage.removeItem('draftUserId');
                            dispatch(initializeUser());
                            return;
                        }
                        dispatch(
                            addNotification({
                                message: res.error as string,
                                severity: 'error',
                            }),
                        );
                    })
                    .catch(() => {
                        dispatch(
                            addNotification({
                                message: t('waitlist_login_failed_consume'),
                                severity: 'error',
                            }),
                        );
                    });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <>
            <div className="waitlist-login__input email">
                <DepInput
                    autoComplete="email"
                    label={t('common:email')}
                    type="email"
                    isInvalid={isInvalid}
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />
            </div>
            <div className="waitlist-login__input">
                <PasswordInput
                    autoComplete="password"
                    isInvalid={false}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                />
            </div>
            <div className="waitlist-login__btn">
                <DepButton size="large" onClick={handleSubmit} loading={loading} disabled={!userId}>
                    {t('common:lets_do_this')}
                </DepButton>
            </div>
            <Typography variant="div" type="label" classes={['waitlist-login__link']}>
                <span onClick={goForgotPassword}>{t('waitlist_login_forgot')}</span>
            </Typography>
        </>
    );
};

export default WaitListLoginForm;
